<template>
  <v-app>
    <Header ref="header"/>
    <Sidebar/>
    <v-content>
      <v-container fluid py-2 px-3 style="padding-bottom: 0 !important;">
        <v-layout wrap>
          <v-flex text-xs-left xs12 md4 px-3 v-if="showOrderList">
            <v-text-field v-model="search" append-icon="search" placeholder="Search" v-if="!currentOrder" style="padding-bottom: 0 !important;"/>
          </v-flex>
          <v-flex text-xs-right xs12 md8 v-if="showOrderList">
            <v-btn flat @click="create" class="bw-6">Create new order</v-btn>
          </v-flex>
          <v-flex text-xs-left xs12 md4 v-if="showOrder">
            <v-btn flat v-if="canCreateContract" @click="createContract" class="bw-5">Create contract</v-btn>
            <v-btn flat v-if="hasContract" @click="displayContract(contract)" class="bw-4">Contract</v-btn>
          </v-flex>
          <v-flex text-xs-right xs12 md8 v-if="showOrder">
            <v-btn flat @click="save" :disabled="saveButtonDisabled" class="bw-3" v-if="hasItems">Save</v-btn>
            <v-btn flat @click="createItems" :disabled="createItemsButtonDisabled" class="bw-3" v-if="!hasItems">Create</v-btn>
            <v-btn flat @click="addItem" :disabled="addItemButtonDisabled" class="bw-4" v-if="hasItems">Add item</v-btn>
            <v-btn flat @click="confirmDeleteDialog = true" v-if="canDelete" class="bw-3">Delete</v-btn>
            <v-btn flat @click="cancel" class="bw-3">{{ currentOrder.id > 0 ? 'Close' : 'Cancel' }}</v-btn>
          </v-flex>
          <v-flex text-xs-left xs12 md4 v-if="showContract && !sendContractStarted">
            <v-btn flat v-if="canPrintContract" @click="printContract" :disabled="contractDirty" class="bw-3">Print</v-btn>
            <v-btn flat v-if="canSendContract" @click="startSendContract" :disabled="contractDirty" class="bw-3">Send</v-btn>
          </v-flex>
          <v-flex text-xs-right xs12 md8 v-if="showContract && !sendContractStarted">
            <v-btn flat @click="saveContract" class="bw-3" :disabled="!canSaveContract">Save</v-btn>
            <v-btn flat @click="confirmDeleteContractDialog = true" v-if="canDeleteContract" class="bw-3">Delete</v-btn>
            <v-btn flat @click="cancelContract" class="bw-3">{{ currentContract.id > 0 ? 'Close' : 'Cancel' }}</v-btn>
          </v-flex>
          <v-flex text-xs-right xs12 v-if="showContract && sendContractStarted">
            <v-btn flat @click="sendContractStarted = false" class="bw-3">Cancel</v-btn>
          </v-flex>
          <v-flex text-xs-left xs12 md6 px-3 style="padding-top: 6px;" v-if="showOrderList">
            <v-autocomplete :items="filterCustomers" v-model="filterCustomer" item-text="name" prepend-icon="a-store" return-object @change="loadOrders" class="customerFilter"/>
          </v-flex>
          <v-flex text-xs-left xs12 md6 px-3 style="padding-top: 6px;" v-if="showOrderList">
            <v-autocomplete :items="filterCategories" v-model="filterCategory" item-text="displayLabel" prepend-icon="a-trees" return-object @change="loadOrders" class="categoryFilter">
              <template slot="selection" slot-scope="data">
                <div v-if="data.item.color && !(data.item.color.secondColor && data.item.color.secondColor !== '')" :style="'margin: 3px 10px 3px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background-color: #' + data.item.color.color + ';'"></div>
                <div v-if="data.item.color && data.item.color.secondColor && data.item.color.secondColor !== ''" :style="'margin: 3px 10px 3px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background: repeating-linear-gradient(-45deg, #' + data.item.color.color + ', #' + data.item.color.color + ' 3px, #' + data.item.color.secondColor + ' 3px, #' + data.item.color.secondColor + ' 6px);'"></div>
                {{ data.item.displayLabel }}
              </template>
              <template slot="item" slot-scope="data">
                <div v-if="data.item.color && !(data.item.color.secondColor && data.item.color.secondColor !== '')" :style="'margin: 3px 10px 3px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background-color: #' + data.item.color.color + ';'"></div>
                <div v-if="data.item.color && data.item.color.secondColor && data.item.color.secondColor !== ''" :style="'margin: 3px 10px 3px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background: repeating-linear-gradient(-45deg, #' + data.item.color.color + ', #' + data.item.color.color + ' 3px, #' + data.item.color.secondColor + ' 3px, #' + data.item.color.secondColor + ' 6px);'"></div>
                {{ data.item.displayLabel }}
              </template>
            </v-autocomplete>
          </v-flex>
        </v-layout>
      </v-container>
      <v-container fluid py-2 px-3 v-show="showOrderList">
        <v-layout style="max-height: calc(100vh - 190px);">
          <v-flex text-xs-right style="border: 1px solid #DDDDDD; border-radius: 3px; overflow-y: auto;">
            <v-data-table :headers="headers" :items="ordersWithTotal" :search="search" hide-actions no-data-text="No orders found" class="orderTable">
              <template v-slot:items="props">
                <tr @click="selectRow(props.item)" :class="'selectable' + (props.item.contract ? ' customerOrderContract' : '')" v-if="props.item.customer">
                  <td class="text-xs-left" style="padding-top: 1px !important;">{{ props.item.customer.name }}</td>
                  <td class="text-xs-left" style="padding-top: 1px !important;">{{ props.item.number }}</td>
                  <td class="text-xs-left" style="padding-top: 1px !important; width: 125px; white-space: nowrap;">{{ props.item.date }}</td>
                  <td class="text-xs-left" style="padding-top: 1px !important; width: 60px; white-space: nowrap;">{{ props.item.contract ? 'Yes' : 'No' }}</td>
                  <td class="text-xs-left nowrap">
                    <div v-html="props.item.categories"></div>
                  </td>
                  <td class="text-xs-right nowrap" style="width: 180px; padding-right: 15px !important;">
                    <div v-html="props.item.amounts"></div>
                    <div style="width: 100%;">
                      <div class="subTotal" style="display: inline-flex; float: left;">Subtotal</div>
                      <div class="subTotalValue" style="display: inline-flex; float: right; padding-left: 30px;">{{ props.item.total.toLocaleString() }}</div>
                    </div>
                  </td>
                </tr>
                <tr v-if="props.item.type === 'total'" class="total">
                  <td class="text-xs-right">&nbsp;</td>
                  <td class="text-xs-right">&nbsp;</td>
                  <td class="text-xs-right">&nbsp;</td>
                  <td class="text-xs-right">&nbsp;</td>
                  <td class="text-xs-right" style="height: 23px !important; padding-top: 12px !important;">Total</td>
                  <td class="text-xs-right" style="height: 23px !important; padding-top: 12px !important; width: 100px !important; padding-right: 15px !important;">{{ props.item.value.toLocaleString() }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-container>
      <v-container fluid py-0 px-0 v-if="showOrder">
        <v-layout style="max-height: calc(100vh - 160px);">
          <v-flex style="overflow-y: auto;">
            <Panel>
              <PanelColumn title="Order">
                <v-flex xs12 sm4 lg2 style="height: 35px;">
                  <v-label>Customer</v-label>
                </v-flex>
                <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;">
                  <v-select :items="customers" v-model="currentOrder.customer" item-text="name" return-object/>
                </v-flex>
                <v-flex xs12 sm4 lg2 style="height: 35px;" v-if="!hasItems">
                  <v-label>Categories</v-label>
                </v-flex>
                <v-flex xs12 sm8 lg10 v-if="!hasItems">
                  <v-autocomplete :items="categories" v-model="selectedCategories" item-text="displayLabel" multiple return-object>
                    <template slot="selection" slot-scope="data">
                      <div style="padding-right: 30px;">
                        <div v-if="data.item.color && !(data.item.color.secondColor && data.item.color.secondColor !== '')" :style="'display: inline-block; margin: 5px 10px 1px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background-color: #' + data.item.color.color + ';'"></div>
                        <div v-if="data.item.color && data.item.color.secondColor && data.item.color.secondColor !== ''" :style="'display: inline-block; margin: 5px 10px 1px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background: repeating-linear-gradient(-45deg, #' + data.item.color.color + ', #' + data.item.color.color + ' 3px, #' + data.item.color.secondColor + ' 3px, #' + data.item.color.secondColor + ' 6px);'"></div>
                        <div style="display: inline-block; vertical-align: top; padding-top: 5px;">{{ data.item.displayLabel }}</div>
                      </div>
                    </template>
                    <template slot="item" slot-scope="data">
                      <div style="width: 30px; padding-right: 10px !important;">
                        <v-checkbox :input-value="itemSelected(selectedCategories, data.item)" color="primary" style="width: 30px;"/>
                      </div>
                      <div v-if="data.item.color && !(data.item.color.secondColor && data.item.color.secondColor !== '')" :style="'margin: 3px 10px 3px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background-color: #' + data.item.color.color + ';'"></div>
                      <div v-if="data.item.color && data.item.color.secondColor && data.item.color.secondColor !== ''" :style="'margin: 3px 10px 3px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background: repeating-linear-gradient(-45deg, #' + data.item.color.color + ', #' + data.item.color.color + ' 3px, #' + data.item.color.secondColor + ' 3px, #' + data.item.color.secondColor + ' 6px);'"></div>
                      {{ data.item.displayLabel }}
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex xs12 sm4 lg2 style="height: 35px;" v-if="hasItems">
                  <v-label>Order no.</v-label>
                </v-flex>
                <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;" v-if="hasItems">
                  <v-text-field v-model="currentOrder.number" maxlength="50" style="max-width: 250px;"/>
                </v-flex>
                <v-flex xs12 sm4 lg2 style="height: 35px;" v-if="hasItems">
                  <v-label>Order date</v-label>
                </v-flex>
                <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;" v-if="hasItems">
                  <v-menu v-model="dateMenu" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field v-model="currentOrder.date" prepend-icon="event" readonly style="width: 120px;" v-on="on"/>
                    </template>
                    <v-date-picker v-model="currentOrder.date" @input="dateMenu = false"></v-date-picker>
                  </v-menu>
                </v-flex>
                <v-flex xs12 sm4 lg2 style="height: 35px;" v-if="hasItems">
                  <v-label>Contract</v-label>
                </v-flex>
                <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;" v-if="hasItems">
                  <v-checkbox color="teal" v-model="currentOrder.contract"/>
                </v-flex>
                <v-flex xs12 v-if="hasItems">
                  <v-label>Comments</v-label>
                </v-flex>
                <v-flex xs12 v-if="hasItems">
                  <v-textarea solo auto-grow flat rows="3" v-model="currentOrder.comment" maxlength="1000"/>
                </v-flex>
              </PanelColumn>
            </Panel>

            <Panel v-if="currentOrder.items" v-for="(item, i) in currentOrder.items" v-bind:key="item.id">
              <v-flex text-xs-right xs12>
                <v-icon v-if="currentOrder && currentOrder.items.length > 1" @click="deleteItem(i)" title="Delete">delete</v-icon>
              </v-flex>
              <PanelColumn :title="'Item no. ' + (i+1)">
                <v-flex xs12 sm3 lg2 style="height: 35px;">
                  <v-label>Category</v-label>
                </v-flex>
                <v-flex xs12 sm9 lg10>
                  <v-autocomplete :items="categories" v-model="item.category" item-text="displayLabel" return-object>
                    <template slot="selection" slot-scope="data">
                      <div v-if="!(data.item.color.secondColor && data.item.color.secondColor !== '')" :style="'margin: 3px 10px 3px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background-color: #' + data.item.color.color + ';'"></div>
                      <div v-if="data.item.color.secondColor && data.item.color.secondColor !== ''" :style="'margin: 3px 10px 3px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background: repeating-linear-gradient(-45deg, #' + data.item.color.color + ', #' + data.item.color.color + ' 3px, #' + data.item.color.secondColor + ' 3px, #' + data.item.color.secondColor + ' 6px);'"></div>
                      {{ data.item.displayLabel }}
                    </template>
                    <template slot="item" slot-scope="data">
                      <div v-if="!(data.item.color.secondColor && data.item.color.secondColor !== '')" :style="'margin: 3px 10px 3px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background-color: #' + data.item.color.color + ';'"></div>
                      <div v-if="data.item.color.secondColor && data.item.color.secondColor !== ''" :style="'margin: 3px 10px 3px 2px; border-radius: 3px; border: 1px solid #BBBBBB; width: 16px; height: 16px; background: repeating-linear-gradient(-45deg, #' + data.item.color.color + ', #' + data.item.color.color + ' 3px, #' + data.item.color.secondColor + ' 3px, #' + data.item.color.secondColor + ' 6px);'"></div>
                      {{ data.item.displayLabel }}
                    </template>
                  </v-autocomplete>
                </v-flex>

                <v-flex xs12 sm3 lg2 style="height: 35px;">
                  <v-label>Fields</v-label>
                </v-flex>
                <v-flex xs12 sm9 lg10 style="margin-bottom: 10px;">
                  <v-select :items="fields" v-model="item.fields" item-text="displayName" multiple return-object v-if="false"></v-select>
                  <v-autocomplete :items="fields" v-model="item.fields" item-text="displayName" multiple return-object>
                    <template slot="selection" slot-scope="data">
                      <div style="padding-right: 30px;">
                        <div style="display: inline-block; vertical-align: top; padding-top: 5px;">{{ data.item.displayName }}</div>
                      </div>
                    </template>
                    <template slot="item" slot-scope="data">
                      <div style="width: 30px; padding-right: 10px !important;">
                        <v-checkbox :input-value="itemSelected(item.fields, data.item)" color="primary" style="width: 30px;"/>
                      </div>
                      {{ data.item.displayName }}
                    </template>
                  </v-autocomplete>
                </v-flex>

                <v-flex xs12 sm3 lg2 style="height: 35px;">
                  <v-label>Amount</v-label>
                </v-flex>
                <v-flex xs12 sm9 lg10 style="height: 35px;">
                  <v-text-field v-model="item.amount" type="number" maxlength="10" style="max-width: 100px;"/>
                </v-flex>

                <v-flex xs12 sm3 lg2 style="height: 35px;">
                  <v-label>Price</v-label>
                </v-flex>
                <v-flex xs12 sm9 lg10 style="height: 35px;">
                  <v-text-field v-model="item.price" type="text" maxlength="15" style="max-width: 100px;"/>
                </v-flex>

                <v-flex xs12 sm3 lg2>
                  <v-label>Pallet amount</v-label>
                </v-flex>
                <v-flex xs12 sm9 lg10>
                  <v-text-field color="teal" v-model="item.palletAmount" maxlength="10" style="max-width: 100px;"></v-text-field>
                </v-flex>

                <v-flex xs12 sm3 lg2>
                  <v-label>Half pallet amount</v-label>
                </v-flex>
                <v-flex xs12 sm9 lg10>
                  <v-text-field color="teal" v-model="item.halfPalletAmount" maxlength="10" style="max-width: 100px;"></v-text-field>
                </v-flex>

                <v-flex xs12 sm3 lg2 style="height: 35px;">
                  <v-label>Category name</v-label>
                </v-flex>
                <v-flex xs12 sm9 lg10 style="height: 35px;">
                  <v-text-field v-model="item.categoryName" type="text" maxlength="255" style="max-width: 500px;"/>
                </v-flex>

                <v-flex xs12 sm3 lg2 style="height: 35px;">
                  <v-label>Options</v-label>
                </v-flex>
                <v-flex xs12 sm9 lg10 style="height: 35px;">
                  <v-select :items="options" v-model="item.options" item-text="name" return-object multiple/>
                </v-flex>

                <v-flex xs12>
                  <v-label>Comments</v-label>
                </v-flex>
                <v-flex xs12>
                  <v-textarea solo auto-grow flat rows="3" v-model="item.comment" maxlength="1000"/>
                </v-flex>
              </PanelColumn>
            </Panel>
          </v-flex>
        </v-layout>
      </v-container>
      <v-container fluid py-0 px-0 v-if="showContract && !sendContractStarted">
        <v-layout style="max-height: calc(100vh - 160px);">
          <v-flex style="overflow-y: auto;">
            <Panel>
              <PanelColumn title="Contract">
                <v-flex xs12 sm4 lg2 style="height: 35px;">
                  <v-label>Number</v-label>
                </v-flex>
                <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;">
                  <div v-if="!currentContract.id" title="Next contract number" style="cursor: help;">
                    {{ currentContract.number }}<v-icon style="font-size: 10px; margin-left: 3px; margin-bottom: 3px;">star</v-icon>
                  </div>
                  <div v-if="currentContract.id">
                    {{ currentContract.number }}
                  </div>
                </v-flex>

                <v-flex xs12 sm4 lg2 style="height: 35px;">
                  <v-label>Place of signature</v-label>
                </v-flex>
                <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;">
                  <v-text-field color="teal" v-model="currentContract.vendorSignaturePlace" style="max-width: 150px;" @change="contractDirty = true" @keyup="contractDirty = true"></v-text-field>
                </v-flex>

                <v-flex xs12 sm4 lg2 style="height: 35px;">
                  <v-label>Signature date</v-label>
                </v-flex>
                <v-flex xs12 sm8 lg10 style="line-height: 26px; height: 35px;">
                  <v-menu v-model="dateMenu" :close-on-content-click="false" :nudge-right="40" lazy transition="scale-transition" offset-y full-width min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field v-model="currentContract.vendorSignatureDate" prepend-icon="event" readonly style="width: 120px;" v-on="on"/>
                    </template>
                    <v-date-picker v-model="currentContract.vendorSignatureDate" @input="dateMenu = false" @change="contractDirty = true"></v-date-picker>
                  </v-menu>
                </v-flex>

                <v-flex xs12>
                  <v-label>Text</v-label>
                </v-flex>
                <v-flex xs12>
                  <v-textarea solo auto-grow flat rows="10" v-model="currentContract.contractText" @change="contractDirty = true" @keyup="contractDirty = true" style="width: 800px; max-width: 800px;"/>
                </v-flex>

                <div style="height: 15px;">&nbsp;</div>

                <v-flex xs12>
                  <v-label>Comments</v-label>
                </v-flex>
                <v-flex xs12>
                  <v-textarea solo auto-grow flat rows="3" v-model="currentContract.comment" maxlength="1000" @change="contractDirty = true" @keyup="contractDirty = true" style="max-width: 800px;"/>
                </v-flex>
              </PanelColumn>
            </Panel>
          </v-flex>
        </v-layout>
      </v-container>
      <v-container fluid py-0 px-0 v-if="currentContract && sendContractStarted">
        <v-layout>
          <v-flex style="overflow-y: auto;">
            <Panel>
              <PanelColumn title="Send contract">
                <v-flex xs12 sm3 lg2 style="height: 35px;">
                  <v-label>E-mails</v-label>
                </v-flex>
                <v-flex xs12 sm9 lg10 style="margin-bottom: 20px;">
                  <v-textarea solo auto-grow flat rows="2" v-model="sendEmail" class="multipleEmails"></v-textarea>
                </v-flex>
                <v-flex xs12 sm3 lg2 style="height: 35px;">
                  <v-label>Subject</v-label>
                </v-flex>
                <v-flex xs12 sm9 lg10 style="height: 35px;">
                  <v-text-field color="teal" v-model="sendSubject" autocomplete="offit"></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-label>Message</v-label>
                </v-flex>
                <v-flex xs12>
                  <wysiwyg v-model="sendMessage" class="approve-shipment"></wysiwyg>
                </v-flex>
                <v-flex xs12 style="padding-top: 10px;">
                  <v-label>Attachment:</v-label>
                  <div style="display: inline; font-weight: bold; margin-left: 30px; cursor: pointer; text-decoration: underline; font-size: 12px;" @click="printContract">Contract no. {{ currentContract.number }}.pdf</div>
                </v-flex>
                <v-flex xs12 style="padding-top: 10px;">
                  <v-btn flat @click="sendContract" :disabled="!sendEmailCorrect" class="bw-3" style="float: right;">Send</v-btn>
                </v-flex>
              </PanelColumn>
            </Panel>
          </v-flex>
        </v-layout>
      </v-container>

    </v-content>
    <Footer/>

    <v-dialog v-model="confirmDeleteDialog" persistent width="350">
      <v-card pa-5>
        <v-card-title class="headline">Delete order</v-card-title>
        <v-card-text v-if="currentOrder">Are you sure you want to delete this order?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="this.delete">Yes</v-btn>
          <v-btn flat @click="confirmDeleteDialog = false">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmDeleteContractDialog" persistent width="350">
      <v-card pa-5>
        <v-card-title class="headline">Delete contract</v-card-title>
        <v-card-text v-if="currentOrder">Are you sure you want to delete this contract?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="this.deleteContract">Yes</v-btn>
          <v-btn flat @click="confirmDeleteContractDialog = false">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ErrorDialog ref="errorDialog"/>
  </v-app>
</template>

<script>
import Header from '../components/Header'
import Sidebar from '../components/Sidebar'
import Footer from '../components/Footer'
import Panel from '../components/Panel'
import ErrorDialog from '../components/ErrorDialog'
import PanelColumn from '../components/PanelColumn'
import PanelColumn2 from '../components/PanelColumn2'
import NumberUtil from '@/assets/javascript/numberutil'
import axios from 'axios'
import uuid from 'uuid/v4'
import DateUtil from '@/assets/javascript/dateutil'
import { mapGetters } from 'vuex'

export default {
  name: 'Orders',
  components: { Header, Sidebar, Footer, Panel, PanelColumn, PanelColumn2, ErrorDialog },
  data () {
    return {
      search: '',
      headers: [
        { text: 'Customer', value: 'customer.name', sortable: false },
        { text: 'No.', value: 'number', sortable: false },
        { text: 'Date', value: 'date', sortable: false },
        { text: 'Contract', value: 'contract', sortable: false },
        { text: 'Category', value: 'categories', sortable: false },
        { text: 'Amount', value: 'amounts', align: 'end', sortable: false, class: 'orderAmountHeader' }
      ],
      orders: [],
      customers: [],
      categories: [],
      options: [],
      selectedCategories: [],
      fields: [],
      filterCategories: [],
      filterCategory: { id: 0 },
      filterCustomers: [],
      filterCustomer: { id: 0 },
      currentOrder: null,
      confirmDeleteDialog: false,
      contract: null,
      contractLoaded: false,
      currentContract: null,
      contractDirty: false,
      sendContractStarted: false,
      sendEmail: '',
      sendSubject: '',
      sendMessage: '',
      confirmDeleteContractDialog: false,
      dateMenu: false
    }
  },
  created: function () {
    this.$store.dispatch('page/setSidebarItem', { key: 'orders' })
    this.loadOrders()
    this.loadFilterCustomers()
    this.loadFilterCategories()
  },
  computed: {
    users: function () {
      return this.$store.state.user.users
    },
    saveButtonDisabled: function () {
      return !this.currentOrder || this.currentOrder.customer.id === 0 || !this.itemsComplete
    },
    createItemsButtonDisabled: function () {
      return !this.currentOrder || this.currentOrder.customer.id === 0 || this.selectedCategories.length === 0
    },
    addItemButtonDisabled: function () {
      return false
    },
    canDelete: function () {
      return this.currentOrder && this.currentOrder.id > 0
    },
    itemsComplete: function () {
      let complete = false
      if (this.currentOrder && this.currentOrder.items && this.currentOrder.items.length > 0) {
        complete = true
        for (let item of this.currentOrder.items) {
          if (!item.category || !item.category.id || !NumberUtil.isZeroOrPositiveInteger(item.amount) || (item.price && item.price !== '' && !NumberUtil.isZeroOrPositiveNumber(item.price))) {
            complete = false
            break
          }
        }
      }
      return complete
    },
    showOrderList: function () {
      return !this.currentOrder && !this.salesOverview
    },
    showOrder: function () {
      return this.currentOrder && !this.showContract
    },
    showContract: function () {
      return this.currentContract
    },
    canDeleteContract: function () {
      return this.currentContract && this.currentContract.id > 0
    },
    ordersWithTotal: function () {
      let data = []
      if (this.orders && this.orders.length > 0) {
        let total = 0
        for (let order of this.orders) {
          data.push(order)
          total += order.total
        }
        data.push({
          type: 'total',
          value: total
        })
      }
      return data
    },
    hasItems: function () {
      return this.currentOrder && this.currentOrder.items.length > 0
    },
    canPrintContract: function () {
      return this.currentContract && this.currentContract.id
    },
    canSendContract: function () {
      return this.currentContract && this.currentContract.id
    },
    canCreateContract: function () {
      return !this.contract && this.hasItems && this.contractLoaded
    },
    hasContract: function () {
      return this.contractLoaded && this.contract != null
    },
    canSaveContract: function () {
      if (this.currentContract) {
        let valuesOk = this.currentContract.contractText && this.currentContract.contractText.trim() !== '' && this.currentContract.vendorSignaturePlace && this.currentContract.vendorSignaturePlace.trim() !== '' && this.currentContract.vendorSignatureDate && this.currentContract.vendorSignatureDate.trim() !== ''
        return valuesOk && (!this.currentContract.id || this.contractDirty)
      }
      return false
    },
    sendEmailCorrect: function () {
      return this.sendEmail && this.sendEmail.trim() !== ''
    },
    ...mapGetters({
      sessionSalesOrganizations: 'session/getSalesOrganizations'
    })
  },
  methods: {
    loadOrders () {
      axios.get('/backend/rest/customer_order/summary?customer=' + this.filterCustomer.id + '&category=' + (this.filterCategory ? this.filterCategory.id : 0))
        .then(response => {
          this.orders = response.data
        })
    },
    loadContract (displayContract = false) {
      this.contractLoaded = false
      this.contract = null
      if (this.currentOrder && this.currentOrder.id) {
        axios.get('/backend/rest/customer_contract/customer_order/' + this.currentOrder.id)
          .then(response => {
            this.contract = response.data
            if (displayContract) {
              this.displayContract(this.contract)
            }
          })
          .finally(() => {
            this.contractLoaded = true
          })
      }
    },
    loadCustomers () {
      this.customers = []
      axios.get('/backend/rest/customer')
        .then(response => {
          this.customers = response.data
        })
    },
    loadCategories () {
      this.categories = []
      axios.get('/backend/rest/category')
        .then(response => {
          this.categories = response.data
        })
    },
    loadOptions () {
      axios.get('/backend/rest/option')
        .then(response => {
          this.options = response.data
        })
    },
    loadFields () {
      this.fields = []
      axios.get('/backend/rest/field')
        .then(response => {
          this.fields = response.data
          this.fields.sort(function (f1, f2) {
            return f1.displayName.localeCompare(f2.displayName)
          })
          this.fields.splice(0, 0, {
            id: 0,
            address: {
              nameAndText: ''
            }
          })
        })
    },
    loadFilterCustomers () {
      axios.get('/backend/rest/customer')
        .then(response => {
          let filterCustomers = response.data
          filterCustomers.splice(0, 0, {
            id: 0,
            name: ''
          })
          filterCustomers.sort(function (f1, f2) {
            return f1.name.localeCompare(f2.name)
          })
          this.filterCustomers = filterCustomers
        })
    },
    loadFilterCategories () {
      axios.get('/backend/rest/category')
        .then(response => {
          this.filterCategories = response.data
          this.filterCategories.splice(0, 0, {
            id: 0,
            displayLabel: ''
          })
        })
    },
    selectRow (item) {
      this.loadCustomers()
      this.loadCategories()
      this.loadOptions()
      this.loadFields()
      axios.get('/backend/rest/customer_order/' + item.id)
        .then(response => {
          this.currentOrder = response.data
          this.loadContract()
        })
    },
    create () {
      this.loadCustomers()
      this.loadCategories()
      this.loadOptions()
      this.loadFields()
      this.selectedCategories = []
      this.currentOrder = {
        id: 0,
        number: '',
        date: '',
        customer: { id: 0 },
        items: [],
        comment: ''
      }
    },
    addItem () {
      this.currentOrder.items.push(
        {
          id: -this.currentOrder.items.length,
          category: null,
          options: [],
          fields: [],
          amount: '',
          palletAmount: '',
          halfPalletAmount: '',
          price: '',
          categoryName: ''
        }
      )
    },
    save () {
      if (this.currentOrder.items) {
        for (let item of this.currentOrder.items) {
          if (!(item.price && item.price > 0)) {
            item.price = null
          }
        }
      }
      axios.post('/backend/rest/customer_order', this.currentOrder)
        .then(response => {
          this.loadOrders()
          this.currentOrder = null
        })
        .catch(error => {
          console.log(error.response.data)
          this.$refs.errorDialog.errorTitle = error.response.data.title
          this.$refs.errorDialog.errorMessage = error.response.data.message
          this.$refs.errorDialog.errorDialog = true
        })
    },
    createItems () {
      let itemId = 0
      for (let cat of this.selectedCategories) {
        this.currentOrder.items.push(
          {
            id: itemId,
            category: cat,
            options: [],
            fields: [],
            amount: '',
            palletAmount: '',
            halfPalletAmount: '',
            price: '',
            categoryName: ''
          }
        )
        itemId--
      }
    },
    delete () {
      axios.delete('/backend/rest/customer_order/' + this.currentOrder.id)
        .then(response => {
          this.loadOrders()
        })
        .catch(error => {
          console.log(error.response.data.message)
        })

      this.confirmDeleteDialog = false
      this.currentOrder = null
    },
    cancel () {
      this.currentOrder = null
      this.loadOrders()
    },
    canDeleteItem (index) {
      return this.currentOrder.items.length > 1
    },
    deleteItem (index) {
      this.currentOrder.items.splice(index, 1)
    },
    itemSelected (items, item) {
      if (item && items && items instanceof Array) {
        for (let i of items) {
          if (i instanceof Object) {
            if (i.id === item.id) {
              return true
            }
          } else if (i === item.id) {
            return true
          }
        }
      }
      return false
    },
    printContract () {
      let url = '/backend/print/customerContract.xhtml?customerContractId=' + this.currentContract.id + '&title=Contract no. ' + this.currentContract.number + '&uuid=' + uuid()
      window.open(url)
    },
    startSendContract () {
      this.sendContractStarted = true
      this.sendEmail = this.currentContract.customer.email
      let locale = this.currentContract.customer.locale
      if (locale === '') {
        locale = 'en'
      }
      this.sendSubject = ''
      this.sendMessage = ''
      axios.get('/backend/rest/customer_contract/' + this.currentContract.id + '/mail_info')
        .then(response => {
          this.sendSubject = response.data.subject
          this.sendMessage = response.data.message
        })
    },
    createContract () {
      this.currentContract = {
        id: null,
        customer: {
          id: this.currentOrder.customer.id
        },
        customerOrder: {
          id: this.currentOrder.id
        },
        number: '',
        contractText: '',
        vendorSignaturePlace: '',
        vendorSignatureDate: DateUtil.dateToString(new Date()),
        comment: ''
      }
      this.contractDirty = false

      let salesOrgId = this.currentOrder.customer.salesOrganization.id
      let locale = this.currentOrder.customer.locale

      // Initialize the next contract number
      axios.get('/backend/rest/sales_organization/' + salesOrgId)
        .then(response => {
          let salesOrganization = response.data
          this.currentContract.number = salesOrganization.customerContractNumberSequence
        })

      // Initialize the contract text
      axios.get('/backend/rest/configuration/TemplateCustomerContractText_' + locale + '?salesOrganization=' + salesOrgId)
        .then(response => {
          this.currentContract.contractText = response.data.value
        })
    },
    displayContract (contract) {
      this.currentContract = JSON.parse(JSON.stringify(contract))
      this.contractDirty = false
    },
    saveContract () {
      axios.post('/backend/rest/customer_contract', this.currentContract)
        .then(response => {
          this.loadContract(true)
        })
        .catch(error => {
          console.log(error.response.data)
          this.$refs.errorDialog.errorTitle = error.response.data.title
          this.$refs.errorDialog.errorMessage = error.response.data.message
          this.$refs.errorDialog.errorDialog = true
        })
    },
    deleteContract () {
      axios.delete('/backend/rest/customer_contract/' + this.currentContract.id)
        .then(response => {
          this.confirmDeleteContractDialog = false
          this.currentContract = null
          this.contract = null
        })
        .catch(error => {
          console.log(error.response.data.message)
        })
    },
    cancelContract () {
      this.currentContract = null
    },
    sendContract () {
      let contractSend = {
        contractId: this.currentContract.id,
        email: this.sendEmail,
        subject: this.sendSubject,
        message: this.sendMessage
      }
      axios.post('/backend/rest/customer_contract/send', contractSend)
        .then(response => {
          this.$refs.header.showSnackbar('Contract has been sent to customer')
          this.sendContractStarted = false
          this.loadContract(true)
        })
        .catch(error => {
          console.log(error.response.data)
          this.$refs.errorDialog.errorTitle = error.response.data.title
          this.$refs.errorDialog.errorMessage = error.response.data.message
          this.$refs.errorDialog.errorDialog = true
        })
    }
  },
  watch: {
    sessionSalesOrganizations () {
      this.loadOrders()
      this.loadFilterCustomers()
      this.loadFilterCategories()
    }
  }
}
</script>
<style scoped>
  html {
    overflow-y: auto;
  }
  .v-input {
    padding-top: 0;
    margin-top: 0;
  }
  tr.total td {
    font-weight: bold;
    color: rgba(0, 0, 0, .54)
  }
  div.subTotal {
    font-weight: bold;
    color: rgba(0, 0, 0, .54);
    padding-bottom: 10px;
  }
  div.subTotalValue {
    border-top: 1px solid rgba(0, 0, 0, .54);
    font-weight: bold;
    color: rgba(0, 0, 0, .54);
    padding-bottom: 10px;
  }
</style>



// WEBPACK FOOTER //
// src/pages/Orders.vue